import $ from 'jquery';
import {svg} from '../svg.ts';
import {ensureCommentFormExists, findCommentForm, scrollIntoView} from './comment-tools.ts';
import {attachSnippet} from './attached-snippet.ts';
import {clientRectToBasePercent, parseViewBoxString} from './selectregion_coords.ts';
import {activateOptionInGroup, isDescendantOf, vueInstance} from '../domutils.ts';
import {showTemporaryTooltip} from '../modules/tippy.ts';
import {event} from '../analytics.ts';
import {clamp} from '../js_utils.ts';
import {useLayersStore} from '../allspice_app/stores/layers.ts';
import {useDiffToggleStore} from '../allspice_app/stores/diffToggle.ts';

// The expected DOM structure looks like this.
//
// .diff-file-box                <-- SelectRegionFileState associated with this.
//   .selectable-region-ancestor
//     allspice-diff             <-- AllSpiceDiffView.vue
//       .selectable-region-parent
//         .selectable-region    <-- overflow: hidden
//           .pan-zoom-container
//             .pan-zoom           <-- Scale and translate are applied here.
//               svg
//               .selection-region <-- The box that highlights the selection.
//         .selection-region-button <-- Close button, needs to overflow.
//         .selection-region-button <-- Add comment button, needs to overflow.
//
// With comments added.  Comments live inside diff-file-box so they hide when the
// file is collapsed.
//
// .diff-file-box(.select-region-mode)
//   .selectable-region-ancestor
//     allspice-diff
//       .selectable-region-parent
//         .selectable-region
//           .pan-zoom-container
//             .pan-zoom
//               svg
//               .selection-region
//   .code-conversations
//     .conversation-holder          <-- One for each existing comment thread.
//       .comment-list
//       .add-comment
//     .conversation-holder          <-- The last one is the form for a new
//       .add-comment                    thread, loaded via AJAX.  It doesn't
//                                       have a `.comment-list`.
//
// Non-Diff
//
// .diff-file-box => .non-diff-file-content
//
// Legend
//
// .select-region-mode = Crosshair cursor; touch selects instead of scrolling.
// .selectable-region-ancestor = Only descendants are selectable (allspice-diff
//                       can also appear inside comments).
// .pan-zoom-container = Canvas so you can grab it to pan when zoomed out, and it
//                       clips the content when zoomed in.
//
// See repo/diff/box.tmpl.

/**
 * Per-file state, keyed by Element -> SelectRegionFileState.  WeakMap so entries
 * are GC'd when the Element leaves the DOM.  Access via selectRegionState().
 * @type {WeakMap<Element, SelectRegionFileState>}
 */
const stateByElement = new WeakMap();

const _SCALE_REGEXP = /scale\(([^)]+)\)/;

/** Whether to expect and use pan-zoom. */
const IS_PAN_ZOOM_ENABLED = true;

// State that is per file.
class SelectRegionFileState {
  element: HTMLElement;
  selectedPanZoom: HTMLElement | null;
  isDiff: boolean;
  isSelectingRegion: boolean;
  selectionBox: HTMLDivElement | null;
  addCommentButton: HTMLButtonElement | null;
  currentDocId: string;
  copyPermalinkMarkdownButton: HTMLButtonElement | null;
  closeSelectionButton: HTMLButtonElement | null;
  zoomInverseScale: number;
  copyPermalinkMarkdownTooltipText: string;
  copySucceededTooltipText: string;
  copyFailedTooltipText: string;
  selectionPointerCaptureElement: HTMLElement | null;
  selectionPointerCapturePointerId: number;
  selectionStartLeft: number;
  selectionStartTop: number;
  selectionLeft: number;
  selectionTop: number;
  selectionRight: number;
  selectionBottom: number;
  selectionAttrs: {
    viewCoords: number[] | null;
    fullWidth: number;
    fullHeight: number;
    isFlipped: boolean;
  } | null;
  layersStore: ReturnType<typeof useLayersStore>;
  diffToggleStore: ReturnType<typeof useDiffToggleStore>;

  /**
   * @param {HTMLElement} element
   */
  constructor(element: HTMLElement) {
    /**
     * The parent diff-file-box Element instance that this is attached to.
     */
    this.element = element;
    /**
     * The current pan-zoom container where the selection is
     */
    this.selectedPanZoom = null;
    this.isDiff = element.classList.contains('diff-file-box');
    this.isSelectingRegion = false;
    /**
     * Element that is the selection highlight.
     */
    this.selectionBox = null;
    // Element that we capture the pointer onto.  We need this to cancel it
    // later.
    this.selectionPointerCaptureElement = null;
    this.selectionPointerCapturePointerId = -1;
    // Pointer coordinates of where the selection started.
    this.selectionStartLeft = 0;
    this.selectionStartTop = 0;
    // Pointer coordinates of the selection.
    this.selectionLeft = 0;
    this.selectionTop = 0;
    this.selectionRight = 0;
    this.selectionBottom = 0;
    // Selection attributes saved to markdown.  viewCoords: percentages
    // [left, top, right, bottom], or null when there's no selection.
    this.selectionAttrs = null;

    /**
     * Element that has the plus icon next to the selection box to add a new
     * comment.
     */
    this.addCommentButton = null;
    /**
     * The id of the last sub element (i.e. document)
     * where we showed the select region comment button
     */
    this.currentDocId = '';
    /**
     * Element that has the copy icon next to the selection box to copy
     * permalink markdown for the selection.
     */
    this.copyPermalinkMarkdownButton = null;
    /**
     * Element with an X icon to close the selection.
     */
    this.closeSelectionButton = null;

    /**
     * Factor to scale that cancels out the current zoom.
     */
    this.zoomInverseScale = 1;

    this.copyPermalinkMarkdownTooltipText = element.getAttribute('data-copy-permalink-markdown-tooltip') ?? '';
    this.copySucceededTooltipText = element.getAttribute('data-copy-succeeded-tooltip') ?? '';
    this.copyFailedTooltipText = element.getAttribute('data-copy-failed-tooltip') ?? '';

    /**
     * We need to know which layers are active when we make a selection
     */
    this.layersStore = useLayersStore();
    this.diffToggleStore = useDiffToggleStore();
  }

  isFlipped() {
    return this.element.classList.contains('flipped');
  }

  // Triggered by: click (of the pointer mode button)
  clickPointerMode(button: Element) {
    const isSelectMode = this.setSelectRegionMode(false, button);
    if (!isSelectMode) {
      this.clearSelectionRegion();
    }
  }

  // Triggered by: click (of the select mode button)
  clickSelectMode(button: Element) {
    this.setSelectRegionMode(true, button);
  }

  /**
   * @private
   * @param {boolean | undefined} newSelectMode The new mode to use.  True is select mode.
   * @param {Element | null} [button=null] Element that was clicked
   * @return {boolean} the new mode: true if selecting region mode.
   */
  setSelectRegionMode(newSelectMode: boolean | undefined, button: Element | null = null) {
    // Don't allow toggling with undefined.
    const selectMode = newSelectMode ?? false;
    this.element.classList.toggle('select-region-mode', selectMode);
    if (!button) {
      const buttonSelector = newSelectMode ?
        '.select-region-select-mode' :
        '.select-region-pointer-mode';
      button = this.element.querySelector(buttonSelector);
    }
    if (!button) {
      throw new Error("Couldn't find the button group for select region mode");
    }
    // Activate the button and deactivate sibling buttons.
    activateOptionInGroup(button);

    // Notify of mode change.
    const changeEvent = new CustomEvent('selectregionmodechange', {
      bubbles: true,
      detail: {
        isSelectMode: newSelectMode,
      },
    });
    this.element.dispatchEvent(changeEvent);

    return newSelectMode;
  }

  /**
   * Triggered by: pointerdown
   * @param {Element} elem
   * @param {number} pointerId
   * @param {number} newX
   * @param {number} newY
   */
  startSelectingRegion(elem: Element, pointerId: number, newX: number, newY: number) {
    this.hideSelectionButtons();

    const selectableRegion = elem.closest('.selectable-region');
    if (!selectableRegion) throw new Error('expected selectable-region to exist');
    const docId = selectableRegion.getAttribute('data-doc-id') || '';
    const recreateSelection = docId !== this.currentDocId;

    const panZoom = selectableRegion.querySelector('.pan-zoom');
    if (!panZoom) throw new Error('expected pan-zoom to exist');
    this.selectedPanZoom = panZoom as HTMLElement;

    let selectionBoxJustCreated = false;
    // Reuse an existing highlight so the user can't create multiple at once.
    if (!this.selectionBox || recreateSelection) {
      if (this.selectionBox) {
        this.selectionBox.remove();
      }
      // Create the selection highlight element.
      selectionBoxJustCreated = true;
      this.selectionBox = document.createElement('div');
      this.selectionBox.className = 'selection-region';
      this.selectionBox.addEventListener('click', this.clickSelectionBox.bind(this));
    }
    // New selection: don't link to the old selection's comment.
    this.setSelectionHasComment(false);
    // Needed later (pointerup, coordinate conversions), not right now.
    this.reloadPanZoomState();

    // Legacy CSS-transform mode only: there the start coords are SVG-relative (mirrored on a flip).
    // In viewBox mode getScreenCTM applies the flip on save, so flipping here too double-applies it.
    // A .pan-zoom transform signals legacy mode.
    if (this.isFlipped() && this.parseTransform(this.selectedPanZoom.style.transform)) {
      // Computed CSS value gives the unscaled width.
      const panZoomWidth = parseFloat(getComputedStyle(panZoom).width);
      newX = panZoomWidth - newX;
    }

    // Move it into position.
    const selectionBoxStyle = this.selectionBox.style;
    selectionBoxStyle.width = '0';
    selectionBoxStyle.height = '0';
    this.selectionStartLeft = newX;
    this.selectionStartTop = newY;
    this.selectionLeft = this.selectionRight = newX;
    this.selectionTop = this.selectionBottom = newY;
    selectionBoxStyle.left = `${this.selectionStartLeft}px`;
    selectionBoxStyle.top = `${this.selectionStartTop}px`;
    // Append only *after* setting all its styles.
    if (selectionBoxJustCreated) {
      elem.append(this.selectionBox);
    }

    // Capture the pointer so drags off the element still fire events; save
    // state so another event (e.g. keyboard) can release the capture.
    this.isSelectingRegion = true;
    this.selectionPointerCaptureElement = elem as HTMLElement;
    this.selectionPointerCapturePointerId = pointerId;
    (elem as HTMLElement).setPointerCapture(pointerId);
  }

  /**
   * Triggered by: pointermove
   * @param {number} newX
   * @param {number} newY
   * @param {{width: number, height: number}} bounds
   */
  updateSelectionRegion(newX: number, newY: number, bounds: {width: number; height: number}) {
    if (!this.isSelectingRegion) {
      console.error('should only be called while the user is selecting');
      return;
    }

    const {width, height} = bounds;
    const left = clamp(0, Math.min(this.selectionStartLeft, newX), width);
    const right = clamp(Math.max(this.selectionStartLeft, newX), 0, width);
    const top = clamp(0, Math.min(this.selectionStartTop, newY), height);
    const bottom = clamp(Math.max(this.selectionStartTop, newY), 0, height);

    if (!this.selectionBox) throw new Error('expected selection box');
    const selectionBoxStyle = this.selectionBox.style;
    selectionBoxStyle.left = `${left}px`;
    selectionBoxStyle.top = `${top}px`;
    selectionBoxStyle.width = `${right - left}px`;
    selectionBoxStyle.height = `${bottom - top}px`;
    this.selectionLeft = left;
    this.selectionTop = top;
    this.selectionRight = right;
    this.selectionBottom = bottom;
  }

  /**
   * Release pointer capture and reset state.  If cancel is truthy or
   * the size of the selection is zero, the selection is removed.
   *
   * Triggered by: pointerup, pointercancel, keydown (Escape)
   * @param {boolean} cancel
   */
  stopSelectingRegion(cancel: boolean) {
    // Stop capturing.
    if (this.selectionPointerCaptureElement) {
      try {
        this.selectionPointerCaptureElement.releasePointerCapture(this.selectionPointerCapturePointerId);
      } catch (e) {
        console.error('error trying to release the capture', e);
      }
      this.selectionPointerCaptureElement = null;
      this.selectionPointerCapturePointerId = -1;
    }

    // Is the end point the same as the start point?
    const isZeroSize = this.selectionLeft === this.selectionRight &&
      this.selectionTop === this.selectionBottom;
    if (cancel || isZeroSize) {
      this.clearSelectionRegion();
    } else {
      this.saveSelectionRegion();
      this.showSelectionButtons();
    }
    this.isSelectingRegion = false;
    this.selectionStartLeft = 0;
    this.selectionStartTop = 0;
  }

  /**
   * @private
   */
  saveSelectionRegion() {
    this.selectionAttrs = this.computeViewAttributes();
  }

  /**
   * Triggered by: resize
   * @param {UIEvent} _event
   */
  onResize(_event: UIEvent) {
    if (!this.selectionAttrs) return;
    const coords = this.selectionAttrs.viewCoords;
    if (!coords || !this.selectionBox || !this.selectedPanZoom) return;

    const boxStyle = this.selectionBox.style;

    // viewBox mode: map saved % (of the full-document viewBox) through the SVG's current CTM to region-relative pixels.
    if (!this.parseTransform(this.selectedPanZoom.style.transform)) {
      const svg = this.selectedPanZoom.querySelector('svg');
      const base = svg ? parseViewBoxString(svg.getAttribute('data-base-viewbox')) : null;
      const ctm = svg?.getScreenCTM();
      if (svg && base && ctm) {
        const panZoomRect = this.selectedPanZoom.getBoundingClientRect();
        const toRegionPx = (leftPercent: number, topPercent: number) => {
          const pt = svg.createSVGPoint();
          pt.x = base.x + (leftPercent / 100) * base.w;
          pt.y = base.y + (topPercent / 100) * base.h;
          const screen = pt.matrixTransform(ctm);
          return {x: screen.x - panZoomRect.left, y: screen.y - panZoomRect.top};
        };
        const tl = toRegionPx(coords[0], coords[1]);
        const br = toRegionPx(coords[2], coords[3]);
        boxStyle.left = `${Math.min(tl.x, br.x)}px`;
        boxStyle.top = `${Math.min(tl.y, br.y)}px`;
        boxStyle.width = `${Math.abs(br.x - tl.x)}px`;
        boxStyle.height = `${Math.abs(br.y - tl.y)}px`;
        this.updateSelectionButtonsPosition();
        return;
      }
    }

    // Legacy mode (CSS transform).
    const rect = this.selectedPanZoom.getBoundingClientRect();
    let fullWidth = rect.width;
    let fullHeight = rect.height;
    if (IS_PAN_ZOOM_ENABLED) {
      fullWidth *= this.zoomInverseScale;
      fullHeight *= this.zoomInverseScale;
    }
    const result = this.toAbsoluteCoords(fullWidth, fullHeight, coords);
    boxStyle.left = `${result.left}px`;
    boxStyle.top = `${result.top}px`;
    boxStyle.width = `${result.width}px`;
    boxStyle.height = `${result.height}px`;
    this.updateSelectionButtonsPosition();
  }

  /**
   * Remove the selection highlight box.
   */
  clearSelectionRegion() {
    this.hideSelectionButtons();
    if (this.selectionBox) {
      this.selectionBox.remove();
      this.selectionBox = null;
    }
    this.selectionAttrs = null;
  }

  /**
   * @param {Event} event
   */
  clickSelectionBox(event: Event) {
    if (!this.selectionBox || !this.selectionBox.classList.contains('has-comment')) {
      // Respect our state.  Don't try to find the comment thread.
      return;
    }
    event.preventDefault();
    const result = findCommentForm(this.element) as Record<string, unknown>;
    const scrollOptions: ScrollIntoViewOptions = {block: 'center'};
    /** @type {Element | undefined} */
    let inputElem: Element | undefined;
    if (result.editor) {
      inputElem = (result.editor as Record<string, unknown>).textarea as Element | undefined;
    } else if (result.textarea) {
      inputElem = result.textarea as Element | undefined;
    }
    // When there's no comment, there won't be an input element.
    if (inputElem) {
      scrollIntoView(inputElem, scrollOptions);
    }
  }

  /**
   * Returns true if the given element is one of the selection buttons.
   * @param {Node | null | undefined} elem
   * @return {boolean}
   */
  isSelectionButton(elem: Node | null | undefined) {
    return Boolean(elem) && (
      isDescendantOf(elem, this.addCommentButton) ||
      isDescendantOf(elem, this.closeSelectionButton)
    );
  }

  showSelectionButtons() {
    if (!this.selectedPanZoom) return;
    const selectableRegion = this.selectedPanZoom.closest('.selectable-region');
    if (!selectableRegion) throw new Error('expected selectable-region to exist');
    const docId = selectableRegion.getAttribute('data-doc-id') || '';
    const recreateButtons = docId !== this.currentDocId;
    this.currentDocId = docId;
    // Append the buttons to the selectableRegion parent element to make sure
    // they are not hidden by the pan-zoom overflow hidden.
    const selectableRegionParent = selectableRegion.parentNode;
    if (this.isDiff) {
      this.showAddCommentButton(selectableRegionParent, recreateButtons);
    } else {
      this.showCopyPermalinkMarkdownButton(selectableRegionParent, recreateButtons);
    }
    this.showCloseSelectionButton(selectableRegionParent, recreateButtons);
  }

  updateSelectionButtonsPosition() {
    this.updateAddCommentButtonPosition();
    this.updateCopyPermalinkMarkdownButtonPosition();
    this.updateCloseSelectionButtonPosition();
  }

  /**
   * Reset pan-zoom state to the default unzoomed and unpanned state.
   * @private
   */
  resetPanZoomState() {
    this.zoomInverseScale = 1;
  }

  /**
   * Load the pan-zoom state from the DOM.
   * @private
   */
  reloadPanZoomState() {
    if (!IS_PAN_ZOOM_ENABLED) {
      this.resetPanZoomState();
      return;
    }

    const panZoom = this.selectedPanZoom;
    if (!panZoom || !(panZoom as any instanceof HTMLElement ||
      panZoom as any instanceof SVGElement)) {
      this.resetPanZoomState();
      return;
    }

    const transformStr = panZoom.style.transform;
    const transform = this.parseTransform(transformStr);
    // Make sure to not divide by zero.
    if (!transform || !transform.scale) {
      this.resetPanZoomState();
      return;
    }

    this.zoomInverseScale = 1 / transform.scale;
  }

  /**
   * @private
   * @param {string} transformStr value of the transform style
   * @return {{scale: number} | null} returns object with numeric scale, or
   *   null.  If it couldn't be determined or it would result in NaN, null is
   *   returned.
   */
  parseTransform(transformStr: string) {
    if (!transformStr) return null;

    const scaleMatch = _SCALE_REGEXP.exec(transformStr);
    if (!scaleMatch) return null;

    const scaleStr = scaleMatch[1];
    if (!scaleStr) return null;

    const scale = parseFloat(scaleStr);
    if (Number.isNaN(scale)) return null;

    return {scale};
  }

  /**
   * Idempotent
   * @param {HTMLElement} selectableRegionParent the current selectable region parent element
   * @param {boolean} recreate whether we need to recreate the button
   */
  showAddCommentButton(selectableRegionParent: ParentNode, recreate: boolean) {
    let justCreated = false;
    // create a new button if we are on a different document or we haven't yet created a comment button
    if (!this.addCommentButton || recreate) {
      if (this.addCommentButton) {
        this.addCommentButton.remove();
      }

      justCreated = true;
      this.addCommentButton = document.createElement('button');
      this.addCommentButton.className = 'ui primary button selection-region-button';
      this.addCommentButton.innerHTML = svg('octicon-plus');

      this.addCommentButton.addEventListener('click', async (e) => {
        e.preventDefault();
        event('snippet_comment_drafted');
        await this.clickAddComment();
      });
    }

    this.updateAddCommentButtonPosition();

    if (justCreated) {
      // Insert outside the pan-zoom-container so that it can overflow.
      selectableRegionParent.append(this.addCommentButton);
    } else {
      // Make it visible.
      this.addCommentButton.style.display = '';
    }
  }

  /**
   * @private
   */
  updateAddCommentButtonPosition() {
    if (!this.addCommentButton || !this.selectionBox) return;
    // Client rect is affected by the pan-zoom transform.
    const rect = this.selectionBox.getBoundingClientRect();
    const selectionWidth = rect.width;
    const selectionHeight = rect.height;
    const [selectionLeft, selectionTop] =
      this.selectionToSelectableRegionCoords(this.selectionBox);
    const horizontalMargin = 10;
    const verticalMargin = 5;
    const buttonHeight = 22; // Including border.
    const left = selectionLeft + selectionWidth + horizontalMargin;
    this.addCommentButton.style.left = `${left}px`;
    let top = selectionTop + selectionHeight - buttonHeight;
    // Make sure there's space for the close button above it when the selection
    // is very short.
    top = Math.max(top, selectionTop + buttonHeight + verticalMargin);
    this.addCommentButton.style.top = `${top}px`;
  }

  /**
   * Idempotent
   * @param {HTMLElement} selectableRegionParent the current selectable region parent element
   * @param {boolean} recreate whether we need to recreate the button
   */
  showCopyPermalinkMarkdownButton(selectableRegionParent: ParentNode, recreate: boolean) {
    let justCreated = false;
    if (!this.copyPermalinkMarkdownButton || recreate) {
      if (this.copyPermalinkMarkdownButton) {
        this.copyPermalinkMarkdownButton.remove();
      }

      justCreated = true;
      this.copyPermalinkMarkdownButton = document.createElement('button');
      this.copyPermalinkMarkdownButton.className = 'ui primary button selection-region-button';
      const tooltipContainer = document.createElement('div');
      tooltipContainer.setAttribute('data-tooltip-content', this.copyPermalinkMarkdownTooltipText);
      tooltipContainer.innerHTML = svg('octicon-copy');
      this.copyPermalinkMarkdownButton.append(tooltipContainer);
      this.copyPermalinkMarkdownButton.addEventListener('click', async (event) => {
        event.preventDefault();
        this.clickCopyPermalinkMarkdown();
      });
    }

    this.updateCopyPermalinkMarkdownButtonPosition();

    if (justCreated) {
      // Insert outside the pan-zoom-container so that it can overflow.
      selectableRegionParent.append(this.copyPermalinkMarkdownButton);
    } else {
      // Make it visible.
      this.copyPermalinkMarkdownButton.style.display = '';
    }
  }

  /**
   * @private
   */
  updateCopyPermalinkMarkdownButtonPosition() {
    if (!this.copyPermalinkMarkdownButton || !this.selectionBox) return;
    // Client rect is affected by the pan-zoom transform.
    const rect = this.selectionBox.getBoundingClientRect();
    const selectionWidth = rect.width;
    const selectionHeight = rect.height;
    const [selectionLeft, selectionTop] =
      this.selectionToSelectableRegionCoords(this.selectionBox);
    const horizontalMargin = 10;
    const verticalMargin = 5;
    const buttonHeight = 22; // Including border.
    const left = selectionLeft + selectionWidth + horizontalMargin;

    this.copyPermalinkMarkdownButton.style.left = `${left}px`;
    let top = selectionTop + selectionHeight - buttonHeight;
    // Make sure there's space for the close button above it when the selection
    // is very short.
    top = Math.max(top, selectionTop + buttonHeight + verticalMargin);
    this.copyPermalinkMarkdownButton.style.top = `${top}px`;
  }

  /**
   * Idempotent
   * @param {HTMLElement} selectableRegionParent the current selectable region parent element
   * @param {boolean} recreate whether we need to recreate the button
   */
  showCloseSelectionButton(selectableRegionParent: ParentNode, recreate: boolean) {
    let justCreated = false;
    // create a new button if we are on a different document or we haven't yet created a comment button
    if (!this.closeSelectionButton || recreate) {
      if (this.closeSelectionButton) {
        this.closeSelectionButton.remove();
      }

      justCreated = true;
      this.closeSelectionButton = document.createElement('button');
      this.closeSelectionButton.className = 'ui button selection-region-button';
      this.closeSelectionButton.innerHTML = svg('octicon-x');

      this.closeSelectionButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.clickCloseSelection();
      });
    }

    this.updateCloseSelectionButtonPosition();

    if (justCreated) {
      // Insert outside the pan-zoom-container so that it can overflow.
      selectableRegionParent.append(this.closeSelectionButton);
    } else {
      // Make it visible.
      this.closeSelectionButton.style.display = '';
    }
  }

  /**
   * @private
   */
  updateCloseSelectionButtonPosition() {
    if (!this.closeSelectionButton || !this.selectionBox) return;
    const margin = 10;
    // Client rect is affected by the pan-zoom transform.
    const rect = this.selectionBox.getBoundingClientRect();
    const selectionWidth = rect.width;
    const [selectionLeft, selectionTop] =
      this.selectionToSelectableRegionCoords(this.selectionBox);
    this.closeSelectionButton.style.left = `${selectionLeft + selectionWidth + margin}px`;
    this.closeSelectionButton.style.top = `${selectionTop}px`;
  }

  hideSelectionButtons() {
    // Blur so that when the button is shown again, it doesn't have the focus.
    if (this.addCommentButton) {
      this.addCommentButton.blur();
      this.addCommentButton.style.display = 'none';
    }
    if (this.copyPermalinkMarkdownButton) {
      this.copyPermalinkMarkdownButton.blur();
      this.copyPermalinkMarkdownButton.style.display = 'none';
    }
    if (this.closeSelectionButton) {
      this.closeSelectionButton.blur();
      this.closeSelectionButton.style.display = 'none';
    }
  }

  async clickAddComment() {
    const elem = this.element;
    const coords = this.selectionAttrs?.viewCoords;
    if (!this.selectionAttrs || !coords) {
      throw new Error('Expected there to be selection coordinates but found none');
    }
    const lineString = this.lineStringFromCoords(coords);
    const result = await ensureCommentFormExists(elem, lineString);
    const insertText = this.markdownForSelection();
    const editor = result.editor;
    const form = editor.container.closest('form');
    if (!form) {
      throw new Error('Expected the comment editor to be inside a form');
    }
    // The markdown lives in a label above the editor, not in the textarea, so
    // the user can't delete it by accident. It is merged back in on submit.
    // Attach it before scrolling so the layout change happens first.
    attachSnippet(form, insertText, elem.getAttribute('data-path') || '');

    // Scroll to center so that there's some space below for the editor to expand.
    const scrollOptions: ScrollIntoViewOptions = {block: 'center'};

    // eslint-disable-next-line github/no-then
    scrollIntoView(editor.container, scrollOptions).then(() => {
      editor.focus();
    });

    // If everything succeeded, hide the buttons.
    this.hideSelectionButtons();
    // Remove the selection highlight.
    this.clearSelectionRegion();
    // Turn selection mode off.
    this.setSelectRegionMode(false);
    // Make the selection itself clickable.
    this.setSelectionHasComment(true);
  }

  /**
   * @param {boolean} hasComment
   */
  setSelectionHasComment(hasComment: boolean) {
    if (!this.selectionBox) return;
    this.selectionBox.classList.toggle('has-comment', hasComment);
  }

  /**
   * @returns {string | null} the Pull Request index
   */
  findPrIndex() {
    const el = this.element.closest('.diff-file-box');
    if (!el) {
      return null;
    }
    return el.getAttribute('data-pr-index');
  }

  /**
   * Build the markdown for the current selection.  You shouldn't call this if there isn't currently a selection.
   * @returns {string} Markdown
   */
  markdownForSelection() {
    const elem = this.element;
    const headPath = elem.getAttribute('data-path') || '';

    const markdownAttrs: Record<string, string | boolean | null> = {};

    let coords = this.selectionAttrs?.viewCoords;
    if (!this.selectionAttrs || !coords) {
      throw new Error('Expected there to be selection coordinates but found none');
    }

    // If flipped when the snippet was taken, store front-of-board coords so a
    // user can remove `is-flipped=true` from the markdown and get what they expect.
    if (this.selectionAttrs.isFlipped) {
      const [left, top, right, bottom] = coords;
      coords = [100 - right, top, 100 - left, bottom];
    }

    const viewCoordsAttr = coords.map((x) => x.toFixed(1)).join(',');
    const escapedViewCoords = escapeMdAttributeString(viewCoordsAttr);
    markdownAttrs['view-coords'] = escapedViewCoords;

    const isFlippedAttr = this.selectionAttrs.isFlipped ? true : null;
    markdownAttrs['is-flipped'] = isFlippedAttr;

    const pcbLayerToggle = this.findPcbLayerToggle();
    const escapedLayersAttr = pcbLayerToggle ? escapeMdAttributeString(this.layersStore.visibleLayerIds.join(',')) : null;
    markdownAttrs['layers'] = escapedLayersAttr;

    const variantToggle = this.findVariantToggle();
    const variantAttr = variantToggle?.getAttribute('data-variant-id') || 'default';
    markdownAttrs['variant'] = escapeMdAttributeString(variantAttr);

    const aspectRatio = this.selectionAttrs.fullWidth / this.selectionAttrs.fullHeight;
    const escapedAspectRatioAttr = Number.isFinite(aspectRatio) ? escapeMdAttributeString(aspectRatio.toFixed(3)) : null;
    markdownAttrs['aspect-ratio'] = escapedAspectRatioAttr;

    const escapeDocdId = this.currentDocId ? escapeMdAttributeString(this.currentDocId) : null;
    markdownAttrs['doc-id'] = escapeDocdId;

    if (!this.isDiff) {
      // Non-diff.
      const commitId = `${elem.getAttribute('data-repository')}:${elem.getAttribute('data-commit-id')}`;
      markdownAttrs['commit'] = escapeMdAttributeString(commitId);
      markdownAttrs['thumbnail'] = 'float-left';

      return `!snippet[](${escapeMdParens(headPath)}){ ${this.stringifyMarkdownAttrs(markdownAttrs)} }`;
    }

    const basePath = elem.getAttribute('data-base-path') || '';
    const diffAttr = `${elem.getAttribute('data-repository-owner')}/${elem.getAttribute('data-repository-name')}:${elem.getAttribute('data-base-commit')}...${elem.getAttribute('data-head-commit')}`;
    markdownAttrs['diff'] = escapeMdAttributeString(diffAttr);

    const prIndex = this.findPrIndex();
    markdownAttrs['pr'] = prIndex ? escapeMdAttributeString(prIndex) : null;

    markdownAttrs['is-added'] = basePath ? null : true;
    markdownAttrs['is-deleted'] = headPath ? null : true;
    // Don't output base path if it matches head path.
    markdownAttrs['base-path'] = (basePath && basePath !== headPath) ? escapeMdAttributeString(basePath) : null;

    const diffState = {
      full: 'full',
      added: 'added_changed',
      removed: 'removed',
      no: 'none',
    }[this.diffToggleStore.selectedKey];
    markdownAttrs['diff-visibility'] = diffState;

    return `!thumbnail[](${escapeMdParens(headPath)}){ ${this.stringifyMarkdownAttrs(markdownAttrs)} }`;
  }

  stringifyMarkdownAttrs(markdownAttrs: Record<string, string | boolean | null>) {
    return Object.entries(markdownAttrs)
      // Don't quote boolean values, but do quote everything else (even numbers)
      .map(([key, val]) => val === null ? null : `${key}=${typeof val === 'boolean' ? val : `"${val}"`}`)
      .filter((x) => x !== null)
      .join(' ');
  }

  clickCopyPermalinkMarkdown() {
    const markdown = this.markdownForSelection();
    navigator.clipboard.writeText(markdown)
      // eslint-disable-next-line github/no-then
      .then(() => {
        if (!this.copyPermalinkMarkdownButton) return;
        showTemporaryTooltip(this.copyPermalinkMarkdownButton, this.copySucceededTooltipText);
        this.copyPermalinkMarkdownButton.setAttribute('data-content', this.copyPermalinkMarkdownButton.getAttribute('data-original'));
      })
      // eslint-disable-next-line github/no-then
      .catch((err) => {
        console.error('Copy to clipboard failed:', err);
        if (!this.copyPermalinkMarkdownButton) return;
        showTemporaryTooltip(this.copyPermalinkMarkdownButton, this.copyFailedTooltipText);
        this.copyPermalinkMarkdownButton.setAttribute('data-content', this.copyPermalinkMarkdownButton.getAttribute('data-original'));
      });
  }

  clickCloseSelection() {
    this.clearSelectionRegion();
  }

  /**
   * Given an array of percent coordinates, return the string that will be sent
   * to the server as the line number.  If any numbers are NaN, null is
   * returned.
   * @param {Array<number>} coords
   * @return {string | null}
   */
  lineStringFromCoords(coords: number[]) {
    const ints = coords.map((x: number) => {
      // No sign and limited precision (int64-based; verify against the backend).
      const n = clamp(Math.round(x), 0, 100);
      return n.toString().padStart(3, '0');
    });
    if (ints.some(Number.isNaN)) return null;
    return stripExtraLeadingZeros(ints.join(''));
  }

  /**
   * @private
   * @return {PcbLayerToggle | null}
   */
  findPcbLayerToggle() {
    const elem = this.element.querySelector('.pcb-layer-toggle');
    if (!elem) return null;
    const pcbLayerToggle = vueInstance(elem);
    return pcbLayerToggle;
  }

  /**
   * @private
   */
  findDiffToggle() {
    const elem = this.element.querySelector('.allspice-diff-toggle');
    if (!elem) return null;
    const diffToggle = vueInstance(elem);
    if (!diffToggle) return null;
    return diffToggle.exposed;
  }

  /**
   * @private
   * @return {HTMLElement | null}
   */
  findVariantToggle() {
    return this.element.closest('.page-content').querySelector('.variant-dropdown');
  }

  computeViewAttributes() {
    if (!this.selectionBox) {
      throw new Error('There needs to be a selection to compute view attributes.');
    }

    if (!this.selectedPanZoom) {
      throw new Error(`Snippet data missing pan-zoom element: id=${this.element.id}, className=${this.element.className}`);
    }

    // viewBox mode: zoom is the SVG viewBox (no CSS transform), so map the selection's
    // client rect through the SVG CTM into % of the full-document viewBox (zoom-invariant).
    // Same shape as the legacy path, so persisted view-coords stay compatible.
    const fromViewBox = this.computeViewAttributesFromViewBox();
    if (fromViewBox) return fromViewBox;

    const rect = this.selectedPanZoom.getBoundingClientRect();
    const absCoords = [
      this.selectionLeft,
      this.selectionTop,
      this.selectionRight,
      this.selectionBottom,
    ];
    let fullWidth = rect.width;
    let fullHeight = rect.height;
    if (IS_PAN_ZOOM_ENABLED) {
      fullWidth *= this.zoomInverseScale;
      fullHeight *= this.zoomInverseScale;
    }
    return {
      viewCoords: this.toPercentCoords(fullWidth, fullHeight, absCoords),
      fullWidth,
      fullHeight,
      isFlipped: this.isFlipped(),
    };
  }

  /**
   * View attributes from the SVG viewBox, or null in legacy CSS-transform panzoom.
   * getScreenCTM is reliable here because there's no ancestor CSS transform.
   * @private
   */
  computeViewAttributesFromViewBox() {
    if (!this.selectionBox || !this.selectedPanZoom) return null;
    // A CSS transform on .pan-zoom means legacy panzoom is in use.
    if (this.parseTransform(this.selectedPanZoom.style.transform)) return null;
    const svg = this.selectedPanZoom.querySelector('svg');
    if (!svg) return null;
    const base = parseViewBoxString(svg.getAttribute('data-base-viewbox'));
    const ctm = svg.getScreenCTM();
    if (!base || !ctm) return null;

    const viewCoords = clientRectToBasePercent(
      this.selectionBox.getBoundingClientRect(),
      ctm.inverse(),
      base,
    );
    return {
      viewCoords,
      fullWidth: base.w,
      fullHeight: base.h,
      isFlipped: this.isFlipped(),
    };
  }

  /**
   * Pure.
   * @param {number} fullWidth
   * @param {number} fullHeight
   * @param {Array<number>} absCoords
   */
  toPercentCoords(fullWidth: number, fullHeight: number, absCoords: number[]) {
    const leftPercent = 100 * absCoords[0] / fullWidth;
    const topPercent = 100 * absCoords[1] / fullHeight;
    const rightPercent = 100 * absCoords[2] / fullWidth;
    const bottomPercent = 100 * absCoords[3] / fullHeight;
    return [leftPercent, topPercent, rightPercent, bottomPercent];
  }

  /**
   * Pure.
   * @param {number} fullWidth
   * @param {number} fullHeight
   * @param {Array<number>} percentCoords
   */
  toAbsoluteCoords(fullWidth: number, fullHeight: number, percentCoords: number[]) {
    const leftPercent = percentCoords[0];
    const topPercent = percentCoords[1];
    const rightPercent = percentCoords[2];
    const bottomPercent = percentCoords[3];
    const left = fullWidth * (leftPercent / 100);
    const top = fullHeight * (topPercent / 100);
    const right = fullWidth * (rightPercent / 100);
    const bottom = fullHeight * (bottomPercent / 100);
    const width = Math.abs(right - left);
    const height = Math.abs(bottom - top);

    return {left, top, right, bottom, width, height};
  }

  /**
   * Pure.  This specifically works on style/offset coordinates, so translation
   * isn't needed.
   * @param {HTMLElement} elem
   * @returns {Array<number>}
   */
  selectionToSelectableRegionCoords(elem: HTMLElement) {
    if (!IS_PAN_ZOOM_ENABLED) {
      // Style properties are unaffected by zooming.  Assumes pixel units.
      return [
        parseFloat(elem.style.left),
        parseFloat(elem.style.top),
      ];
    }
    const rect = elem.getBoundingClientRect();
    const parent = elem.closest('.selectable-region');
    const parentRect = parent?.getBoundingClientRect() ?? new DOMRect();
    return [
      rect.left - parentRect.left,
      rect.top - parentRect.top,
    ];
  }

  onCommentCancel() {
    this.clearSelectionRegion();
    this.setSelectRegionMode(false);
  }
}

/**
 * Strip off extra zeros from a string representation of an integer.  It may
 * return '0'.
 * @param {string} s
 * @return {string}
 */
function stripExtraLeadingZeros(s: string) {
  let i = 0;
  while (i < s.length - 1 && s[i] === '0') {
    i++;
  }
  return s.slice(i);
}

/**
 * Get or create the state for an Element.
 * @param {Element} elem
 */
function elementState(elem: Element) {
  let state = stateByElement.get(elem);
  if (!state) {
    if (!(elem instanceof HTMLElement)) {
      throw new Error(`Expected elem to be an HTMLElement, but got elem.nodeName=${elem.nodeName}, elem.id=${elem.id}, elem.className=${elem.className}`);
    }
    // Instantiate new state.
    state = new SelectRegionFileState(elem);
    stateByElement.set(elem, state);
  }

  return state;
}

/**
 * Return the per-file element that has the state, or null if it can't be
 * found.
 * @param {Element} elem
 * @return {Element | null}
 */
export function selectRegionParent(elem: Element) {
  return elem.closest('.diff-file-box.has-selectable-region') ||
    elem.closest('.non-diff-file-content.has-selectable-region');
}

/**
 * Convenience helper to find the parent Element where the state is stored, then
 * get the parent's state.
 * @param {Element} elem
 */
function selectRegionState(elem: Element) {
  const parent = selectRegionParent(elem);
  if (!parent) return null;
  return elementState(parent);
}

function pointerEventsTargetSelector() {
  return IS_PAN_ZOOM_ENABLED ?
    '.selectable-region .pan-zoom' :
    '.selectable-region';
}

/**
 * Pointer position in .pan-zoom-local pixels.  offsetX/offsetY are unreliable on SVG
 * children (no HTML offset box), so in viewBox mode (no CSS transform) derive coords from
 * clientX/Y relative to the unscaled .pan-zoom; in legacy CSS-transform mode offsetX/Y work.
 */
function panZoomLocalCoords(
  elem: Element,
  e: {clientX?: number; clientY?: number; offsetX?: number; offsetY?: number},
): {x: number; y: number} {
  const transform = (elem as HTMLElement).style?.transform ?? '';
  if (!_SCALE_REGEXP.test(transform)) {
    const rect = elem.getBoundingClientRect();
    return {x: (e.clientX ?? 0) - rect.left, y: (e.clientY ?? 0) - rect.top};
  }
  return {x: e.offsetX ?? 0, y: e.offsetY ?? 0};
}

export function initSelectionRegion() {
  // Switch into normal pointer mode.
  $(document).on('click', '.select-region-pointer-mode', function(e) {
    e.preventDefault();
    const state = selectRegionState(this);
    if (!state) return;
    state.clickPointerMode(this);
  });
  // Switch into select region mode.
  $(document).on('click', '.select-region-select-mode', function(e) {
    e.preventDefault();
    const state = selectRegionState(this);
    if (!state) return;
    state.clickSelectMode(this);
  });
  // With pan-zoom on, receive events in the panned/zoomed coordinate system.
  const eventTargetSelector = pointerEventsTargetSelector();
  // Begin creating selection region.
  $(document).on('pointerdown', eventTargetSelector, function(e) {
    if (e.buttons !== 1) return;
    const parent = selectRegionParent(this);
    if (!parent) return;
    const isModeSelectRegion = parent.classList.contains('select-region-mode');
    if (!isModeSelectRegion) return;

    const state = elementState(parent);

    // Clicking the selection box shouldn't start a new selection.
    if (state.selectionBox && e.target === state.selectionBox) return;
    // The user can click on the selection buttons.
    if (state.isSelectionButton(e.target)) return;

    e.preventDefault();

    if (e.pointerId === undefined) {
      throw new Error(`pointerdown event is missing pointerId=${e.pointerId}`);
    }

    const coords = panZoomLocalCoords(this, e);
    state.startSelectingRegion(this, e.pointerId, coords.x, coords.y);
  });
  // Change selection region.
  $(document).on('pointermove', eventTargetSelector, function(e) {
    const state = selectRegionState(this);
    if (!state?.isSelectingRegion) return;
    e.preventDefault();

    const coords = panZoomLocalCoords(this, e);
    const {width, height} = this.getBoundingClientRect();
    state.updateSelectionRegion(coords.x, coords.y, {width, height});
  });
  // Finish creating selection region.
  $(document).on('pointerup', eventTargetSelector, function(e) {
    const state = selectRegionState(this);
    if (!state?.isSelectingRegion) return;
    e.preventDefault();
    state.stopSelectingRegion(false);
  });
  // Cancel creating selection region.
  $(document).on('pointercancel', eventTargetSelector, function(e) {
    const state = selectRegionState(this);
    if (!state?.isSelectingRegion) return;
    e.preventDefault();
    state.stopSelectingRegion(true);
  });
  // Cancel via keyboard.  Not scoped to the element since focus may be elsewhere
  // while selecting with a pointing device.
  document.addEventListener('keydown', (e) => {
    // Ignore IME composition.
    if (e.isComposing) return;
    if (e.key !== 'Escape') return;
    let found = false;
    for (const elem of document.querySelectorAll('.diff-file-box, .non-diff-file-content')) {
      const state = stateByElement.get(elem);
      if (state?.isSelectingRegion) {
        found = true;
        state.stopSelectingRegion(true);
      }
    }
    if (found) {
      e.preventDefault();
    }
  });
  // On resize, move selection regions to keep their logical location.
  // eslint-disable-next-line github/prefer-observers
  window.addEventListener('resize', (event) => {
    for (const elem of document.querySelectorAll('.diff-file-box, .non-diff-file-content')) {
      const state = stateByElement.get(elem);
      if (state) {
        state.onResize(event);
      }
    }
  });
}

/**
 * This gets called whenever a code comment gets canceled.  It may or may not be
 * a region comment.
 * @param {Element | null} button The cancel button that was clicked.
 */
export function selectRegionOnCancelCodeComment(button: Element | null) {
  if (!button) return;
  const conversationHolder = button.closest('.conversation-holder');
  if (!conversationHolder) return;
  // If we find existing comments, it's not a new thread, so we don't care.
  const commentList = conversationHolder.querySelector('.comment-list');
  if (commentList) return;

  const state = selectRegionState(conversationHolder);
  // If we're not in a select region file, there's nothing to do.
  if (!state) return;

  state.onCommentCancel();
}

/**
 * Escape markdown for a parenthesized value.
 * @param {string} s
 * @return {string}
 */
function escapeMdParens(s: string) {
  return s.replace(/[()]/g, '\\$&');
}

/**
 * Escape markdown for a double-quoted string attribute value.
 * @param {string} s
 * @return {string}
 */
function escapeMdAttributeString(s: string) {
  return (s
    .replace(/["\\]/g, '\\$&')
    // eslint-disable-next-line no-control-regex -- backspace (since \b is used for word boundaries)
    .replace(/\u0008/g, '\\b')
    .replace(/\f/g, '\\f')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t')
  );
}
